<template>
    <OverrideReportStatusModal :component-id="generatorId" :component-type="ComponentType.Generator">
        <ConditionStatusOverrideModal :component-id="generatorId" :component-type="ComponentType.Generator">
            <ComponentCommentModal :component-id="generatorId" :component-type="ComponentType.Generator">
                <ConditionStatusCommentModal :component-id="generatorId" :component-type="ComponentType.Generator">
                    <ConditionStatusViewRecommendationModal>
                        <ConditionStatusEditRecommendationModal :component-id="generatorId" :component-type="ComponentType.Generator" :functional-location="data?.functionalLocation">
                            <ConditionStatusAddRecommendationModal :component-type="ComponentType.Generator" :component-id="generatorId" :functional-location="data?.functionalLocation">
                                <QueryLoaderGuard :is-pending="isPending" :is-error="isError">
                                    <div class="condition-status-content">
                                        <div class="condition-status-content__master-data-latest-grid">
                                            <MasterDataPanel :show-more-default="masterDataShowMore" :generator-id="generatorId" />
                                            <div class="condition-status-content__master-data-latest-grid__latest-reports">
                                                <ConditionStatusLatestReportsSection :component-id="generatorId" :component-type="ComponentType.Generator" />
                                            </div>
                                        </div>
                                        <ConditionStatusRecommendationSection v-if="userPermissions.canViewGeneratorRecommendations" v-model:collapsed="collapsedState.recommendations.collapsed" :component-id="generatorId" :component-type="ComponentType.Generator" />
                                        <ConditionStatusMeasurementsSection v-model:collapsed="collapsedState.measurements.collapsed" />
                                        <ConditionStatusVisualInspectionsSection v-model:collapsed="collapsedState.visualInspections.collapsed" />
                                        <ConditionStatusAnalysisCommentsSection v-model:collapsed="collapsedState.analysisComments.collapsed" :component-id="generatorId" :component-type="ComponentType.Generator" :headline="t('generators.conditionStatus.content.comments.headline')"/>
                                        <HistoricalTrendsSection v-model:collapsed="collapsedState.historicalTrends.collapsed" />
                                        <ConditionStatusComponentCommentsSection v-model:collapsed="collapsedState.generalComments.collapsed" :component-id="generatorId" :component-type="ComponentType.Generator" />
                                    </div>
                                </QueryLoaderGuard>
                            </ConditionStatusAddRecommendationModal>
                        </ConditionStatusEditRecommendationModal>
                    </ConditionStatusViewRecommendationModal>
                </ConditionStatusCommentModal>
            </ComponentCommentModal>
        </ConditionStatusOverrideModal>
    </OverrideReportStatusModal>
</template>

<script setup lang="ts">
import { computed, toRefs, watch } from 'vue'
import ConditionStatusOverrideModal from '@/components/modals/override/ConditionStatusOverrideModal.vue'
import ConditionStatusCommentModal from '@/components/modals/comment/ConditionStatusCommentModal.vue'
import MasterDataPanel from '@/pages/generators/condition-status/sections/master-data/MasterDataPanel.vue'
import ConditionStatusLatestReportsSection from '@/pages/common/component-status/ConditionStatusLatestReportsSection.vue'
import ConditionStatusMeasurementsSection from '@/pages/generators/condition-status/sections/measurements/ConditionStatusMeasurementsSection.vue'
import ConditionStatusVisualInspectionsSection from '@/pages/generators/condition-status/sections/visual-inspections/ConditionStatusVisualInspectionsSection.vue'
import { useRoute, useRouter } from 'vue-router'
import { provideGeneratorId } from '@/composables/useGeneratorId'
import ConditionStatusRecommendationSection from '@/components/condition-status/sections/recommendations/ConditionStatusRecommendationSection.vue'
import ConditionStatusAddRecommendationModal from '@/components/modals/recommendation/add-recommendation/ConditionStatusAddRecommendationModal.vue'
import ConditionStatusEditRecommendationModal from '@/components/modals/recommendation/edit-recommendation/ConditionStatusEditRecommendationModal.vue'
import ConditionStatusViewRecommendationModal from '@/components/modals/recommendation/view-recommendation/ConditionStatusViewRecommendationModal.vue'
import ConditionStatusAnalysisCommentsSection from '@/components/condition-status/sections/comments/ConditionStatusAnalysisCommentsSection.vue'
import ConditionStatusComponentCommentsSection from '@/components/condition-status/sections/comments/ConditionStatusComponentCommentsSection.vue'
import ComponentCommentModal from '@/components/comments/component-comment/ComponentCommentModal.vue'
import { useGeneratorDetailsQuery } from '@/api/queries/generators/useGeneratorDetailsQuery'
import QueryLoaderGuard from '@/components/guards/QueryLoaderGuard.vue'
import { ComponentType } from '@/configuration/component/component-types'
import { useI18n } from 'vue-i18n'
import HistoricalTrendsSection from '@/pages/generators/condition-status/sections/historical-trends/HistoricalTrendsSection.vue'
import { useGeneratorsExportStateStore } from '@/store/generators/exportStateStore'
import { storeToRefs } from 'pinia'
import { useConditionStatusCollapseStore } from '@/store/generators/conditionStatusCollapseStore'
import { usePermissions } from '@/composables/usePermissions'
import OverrideReportStatusModal from '@/components/modals/override-report-status/OverrideReportStatusModal.vue'

const route = useRoute()
const router = useRouter()

const { t } = useI18n()
const { userPermissions } = usePermissions()

const conditionStatusCollapseStore = useConditionStatusCollapseStore()
const { collapsedState } = storeToRefs(conditionStatusCollapseStore)

const generatorsExportStateStore = useGeneratorsExportStateStore()

const props = defineProps<{
    generatorId: string,
}>()

const { generatorId } = toRefs(props)

const { data, isPending, isError } = useGeneratorDetailsQuery(generatorId)

const masterDataShowMore = computed(() => generatorsExportStateStore.conditionStatusExportParameters.masterDataShowMore === 'true')

const processScrollState = () => {
    if (!route.query.focused || isNaN(Number(route.query.focused))) {
        return
    }

    const focusedState = Number(route.query.focused)
    conditionStatusCollapseStore.setCollapseScrollState(focusedState)

    router.replace({
        query: {},
    })
}

provideGeneratorId(computed(() => props.generatorId))

watch(
    () => props.generatorId,
    () => {
        processScrollState()
    },
    { immediate: true },
)
</script>

<style scoped lang="scss">
.condition-status-content {
    display: flex;
    flex-direction: column;

    gap: $spacing-m;

    &__master-data-latest-grid {
        display: flex;
        gap: $spacing-m;

        &__latest-reports {
            flex: 1;
        }
    }
}
</style>
